import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { RoleService, ModulePermissionDto } from './role.service';

export interface ModulePermission {
    key: string;      // Unique key for the module (e.g., 'analysis')
    label: string;    // Display name (e.g., 'Analysis')
    roles: string[];  // Roles that ALWAYS have access (e.g., ['SystemAdmin', 'Admin'])
    enabledForStaff: boolean; // Configurable toggle for 'Staff'
}

@Injectable({
    providedIn: 'root'
})
export class ModuleService {
    private authService = inject(AuthService);
    private readonly STORAGE_KEY = 'module_permissions';

    // Define available modules
    private defaultModules: ModulePermission[] = [
        { key: 'users', label: 'Users', roles: ['SystemAdmin', 'Admin'], enabledForStaff: false },
        { key: 'reports', label: 'Reports', roles: ['SystemAdmin', 'Admin', 'Staff'], enabledForStaff: false },
        { key: 'projects', label: 'Projects', roles: ['SystemAdmin', 'Admin'], enabledForStaff: false },
        { key: 'analysis', label: 'Analysis', roles: ['SystemAdmin', 'Admin'], enabledForStaff: false },
        { key: 'timelogs', label: 'Time Logs', roles: ['SystemAdmin', 'Admin', 'Staff'], enabledForStaff: true },
        { key: 'profile', label: 'My Profile', roles: ['SystemAdmin', 'Admin', 'Staff'], enabledForStaff: true }
    ];

    getAvailableModules() {
        return this.defaultModules;
    }

    private permissionsSubject = new BehaviorSubject<ModulePermission[]>(this.loadPermissions());
    permissions$ = this.permissionsSubject.asObservable();

    private roleService = inject(RoleService);

    constructor() { }

    private loadPermissions(): ModulePermission[] {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (stored) {
            const saved = JSON.parse(stored);
            // Merge saved config with defaults to handle new modules
            return this.defaultModules.map(def => {
                const found = saved.find((s: any) => s.key === def.key);
                // If found, we trust the stored 'enabled' state.
                // In new system, we should map 'isEnabled' field if we want to be consistent,
                // but for now, let's keep consistent with existing structure.
                return found ? { ...def, enabledForStaff: found.enabledForStaff } : def;
            });
        }
        return this.defaultModules;
    }

    savePermissions(permissions: ModulePermission[]) {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(permissions));
        this.permissionsSubject.next(permissions);
    }

    getPermissions(): ModulePermission[] {
        return this.permissionsSubject.value;
    }

    // Call this on login or app start
    refreshPermissions(): Observable<void> {
        const roleName = this.authService.getUserRole();
        if (!roleName) return of(void 0);

        return this.roleService.getMyPermissions().pipe(
            tap((perms: ModulePermissionDto[]) => {
                const updated = this.defaultModules.map(def => {
                    const permission = perms.find(item => item.moduleKey === def.key);
                    return {
                        ...def,
                        enabledForStaff: permission?.isEnabled ?? false
                    };
                });

                this.savePermissions(updated);
            }),
            map(() => void 0)
        );
    }

    // Check if the current user can access a specific module
    canAccess(moduleKey: string): boolean {
        const role = this.authService.getUserRole();
        if (!role) return false;

        // SystemAdmin always has access to everything
        if (role === 'SystemAdmin') {
            return true;
        }

        const module = this.permissionsSubject.value.find(m => m.key === moduleKey);
        if (!module) return false;

        // Validating against the loaded permissions
        // Re-using 'enabledForStaff' as the dynamic flag for ANY role for now.
        // If the role is in the hardcoded immutable list (e.g. SystemAdmin), we might still want to allow access?
        // OR we strictly follow the DB permissions.
        // Let's strictly follow DB permissions for dynamic behavior, BUT keep safety for SystemAdmin if needed.
        // Currently:
        // if (module.roles.includes(role)) { ... }

        // New Logic: 
        // If we have dynamic permissions loaded, we trust them.
        // 'enabledForStaff' in our local object is now hosting the "Is Enabled For Current User" value.
        return module.enabledForStaff;
    }
}
